// Shell: route parsing, global same-origin <a> click interception for SPA
// navigation, and a fetch wrapper that redirects to /login on 401. Mirrors
// ~/labs/lilac/frontend/components/App.tsx's mechanisms (see AGENTS.md).
import { useCallback, useEffect, useRef, useState } from "preact/hooks"
import { api } from "../lib/api"
import type { User } from "../lib/types"
import { AddArtwork } from "./AddArtwork"
import { ArtworkView } from "./ArtworkView"
import { BoxView } from "./BoxView"
import { ErrorBoundary } from "./ErrorBoundary"
import { Home } from "./Home"
import { Landing } from "./Landing"
import { LegalPage } from "./LegalPage"
import { Login } from "./Login"
import { MembersView } from "./MembersView"
import { Navbar } from "./Navbar"
import { Spinner } from "./Spinner"
import { useToast } from "./Toast"

const PUBLIC_PATHS = new Set(["/login", "/terms", "/privacy"])

function installAuthFetch() {
	const original = window.fetch.bind(window)
	window.fetch = async (...args: Parameters<typeof fetch>) => {
		const res = await original(...args)
		// A 401 from the /api/auth/me probe is how the app *detects* the
		// logged-out state — the router then renders Landing (at "/") or Login
		// itself. Redirecting here would hard-bounce "/" to /login before the
		// Landing branch ever runs. Any other 401 is an expired session.
		const target = args[0]
		const url =
			typeof target === "string"
				? target
				: target instanceof URL
					? target.pathname
					: target.url
		if (
			res.status === 401 &&
			!url.endsWith("/api/auth/me") &&
			!PUBLIC_PATHS.has(window.location.pathname)
		) {
			window.location.href = "/login"
		}
		return res
	}
}
installAuthFetch()

type AuthState =
	| { status: "loading" }
	| { status: "anon" }
	| { status: "authed"; user: User }

interface BoxRoute {
	id: number
	sub: "" | "add" | "members"
}

function parseBoxRoute(path: string): BoxRoute | null {
	const m = path.match(/^\/boxes\/(\d+)(?:\/(add|members))?$/)
	if (!m) return null
	return {
		id: Number(m[1]),
		sub: (m[2] as "add" | "members" | undefined) ?? "",
	}
}

function parseArtworkRoute(path: string): { id: number } | null {
	const m = path.match(/^\/artworks\/(\d+)$/)
	if (!m) return null
	return { id: Number(m[1]) }
}

export function App() {
	const [path, setPath] = useState(window.location.pathname)
	const [authState, setAuthState] = useState<AuthState>({ status: "loading" })
	const [cloudName, setCloudName] = useState("")

	const navigate = useCallback((to: string) => {
		window.history.pushState({}, "", to)
		setPath(new URL(to, window.location.href).pathname)
	}, [])

	// Keep a ref so the global click handler always calls the latest navigate.
	const navigateRef = useRef(navigate)
	navigateRef.current = navigate

	// Global SPA link handler — intercepts same-origin <a> clicks so they
	// don't cause a full page reload.
	useEffect(() => {
		const handler = (event: MouseEvent) => {
			const a = (event.target as Element).closest("a")
			if (!a) return

			// Let links with data-native attribute bypass SPA routing for any
			// link that must be a real navigation (e.g. downloads, external
			// auth redirects) — unused today but kept as an escape hatch.
			if (a.hasAttribute("data-native")) return

			if (
				a.target ||
				a.hasAttribute("download") ||
				event.button !== 0 ||
				event.ctrlKey ||
				event.metaKey ||
				event.shiftKey ||
				event.altKey
			) {
				return
			}

			let url: URL
			try {
				url = new URL(a.href, window.location.href)
			} catch {
				return
			}
			if (url.origin !== window.location.origin) return
			if (url.protocol !== "http:" && url.protocol !== "https:") return

			if (
				url.pathname === window.location.pathname &&
				url.search === window.location.search &&
				url.hash !== ""
			) {
				return
			}

			event.preventDefault()
			navigateRef.current(url.pathname + url.search + url.hash)
		}

		document.body.addEventListener("click", handler)
		return () => document.body.removeEventListener("click", handler)
	}, [])

	useEffect(() => {
		const handler = () => setPath(window.location.pathname)
		window.addEventListener("popstate", handler)
		return () => window.removeEventListener("popstate", handler)
	}, [])

	useEffect(() => {
		api
			.me()
			.then((user) => setAuthState({ status: "authed", user }))
			.catch(() => setAuthState({ status: "anon" }))
	}, [])

	// Cloud name is needed to build any Cloudinary delivery URL; fetch it
	// once we know the user is authenticated.
	useEffect(() => {
		if (authState.status !== "authed") return
		api
			.config()
			.then((c) => setCloudName(c.cloud_name))
			.catch(() => {})
	}, [authState.status])

	const { toast } = useToast()

	// Global error handlers — catches errors that slip past the ErrorBoundary
	// (e.g. errors thrown inside event handlers or unhandled promise rejections).
	useEffect(() => {
		const onError = (event: ErrorEvent) => {
			event.preventDefault()
			toast(event.message || String(event.error))
		}
		const onRejection = (event: PromiseRejectionEvent) => {
			event.preventDefault()
			const reason = event.reason
			toast(reason instanceof Error ? reason.message : String(reason))
		}
		window.addEventListener("error", onError)
		window.addEventListener("unhandledrejection", onRejection)
		return () => {
			window.removeEventListener("error", onError)
			window.removeEventListener("unhandledrejection", onRejection)
		}
	}, [toast])

	// Terms/privacy are public — accessible logged in or out, no navbar, no
	// auth gating — so handle them before anything below depends on
	// authState having resolved.
	if (path === "/terms") {
		return <LegalPage page="terms" />
	}
	if (path === "/privacy") {
		return <LegalPage page="privacy" />
	}

	// If already authenticated, redirect away from /login.
	if (authState.status === "authed" && path === "/login") {
		navigate("/")
		return null
	}

	if (path === "/login") {
		return (
			<Login
				onAuthed={() => {
					// The auth endpoints (login/signup/verify/reset) mirror lilac's
					// response shapes and may not always echo back every user field
					// (e.g. `name`), so re-fetch /api/auth/me as the single source of
					// truth for the authenticated user record.
					api
						.me()
						.then((user) => {
							setAuthState({ status: "authed", user })
							navigate("/")
						})
						.catch(() => setAuthState({ status: "anon" }))
				}}
			/>
		)
	}

	if (authState.status === "loading") {
		return (
			<div class="d-flex justify-content-center align-items-center vh-100">
				<Spinner size={48} />
			</div>
		)
	}

	if (authState.status === "anon") {
		// Logged-out visitors get the marketing page at "/" instead of being
		// bounced straight to /login; every other route still requires auth.
		if (path === "/") {
			return <Landing />
		}
		navigate("/login")
		return null
	}

	const onLogout = async () => {
		try {
			await api.logout()
		} catch {
			// Ignore — we're navigating to /login regardless.
		}
		setAuthState({ status: "anon" })
		navigate("/login")
	}

	let inner: preact.ComponentChildren

	const boxRoute = parseBoxRoute(path)
	const artworkRoute = parseArtworkRoute(path)

	if (path === "/") {
		inner = <Home cloudName={cloudName} navigate={navigate} />
	} else if (boxRoute?.sub === "add") {
		inner = <AddArtwork boxId={boxRoute.id} navigate={navigate} />
	} else if (boxRoute?.sub === "members") {
		inner = <MembersView boxId={boxRoute.id} />
	} else if (boxRoute) {
		inner = <BoxView boxId={boxRoute.id} cloudName={cloudName} />
	} else if (artworkRoute) {
		inner = (
			<ArtworkView
				artworkId={artworkRoute.id}
				navigate={navigate}
				cloudName={cloudName}
			/>
		)
	} else {
		inner = <Home cloudName={cloudName} navigate={navigate} />
	}

	return (
		<ErrorBoundary>
			<Navbar user={authState.user} onLogout={onLogout} />
			<div class="flex-grow-1 overflow-auto">{inner}</div>
		</ErrorBoundary>
	)
}
